import * as React from 'react';
import { Button } from 'react-bootstrap';
import { renderArrows } from './further-considerations-section';

// Lightweight inline markdown used by the Notes fields:
//   **bold**       -> <strong>
//   *italic*       -> <em>
//   _underline_    -> <u>
// Markers never span a newline, so a stray '*' on its own line can't run away.
// This is intentionally minimal (no nesting) — enough for practitioner notes
// while keeping the stored value a plain, human-readable string that the PDF
// packer can still line-count.

export type FormatMarker = '**' | '*' | '_';

const isAlnum = (c: string): boolean => !!c && /[A-Za-z0-9]/.test(c);

// Try to match a marker starting at index i. Returns the inner text and the
// index just past the closing marker, or null when this position isn't a valid
// span. Guards against the common false positives:
//   - a space right inside the markers ("* x *" multiplication, "** x **"),
//   - underline markers touching an alphanumeric on the outside ("T3_free_" /
//     snake_case), and
//   - a marker that would span a newline.
function matchMarker(
  text: string,
  i: number,
  marker: FormatMarker,
): { inner: string; end: number } | null {
  const n = text.length;
  const start = i + marker.length;
  let j = start;
  while (j < n) {
    const c = text[j];
    if (c === '\n') return null;
    if (marker === '**') {
      if (c === '*' && text[j + 1] === '*') break; // closing **
      if (c === '*') return null; // a lone * inside bold — not a span
    } else if (c === marker) {
      break; // closing * or _
    }
    j++;
  }
  if (j >= n) return null; // no closing marker
  const inner = text.slice(start, j);
  if (!inner || /^\s|\s$/.test(inner)) return null; // no empty / space-hugged span
  if (marker === '_') {
    const before = i > 0 ? text[i - 1] : '';
    const after = j + marker.length < n ? text[j + marker.length] : '';
    if (isAlnum(before) || isAlnum(after)) return null; // snake_case guard
  }
  return { inner, end: j + marker.length };
}

// Notes store up/down status as the plain characters ↑ / ↓ (a textarea can't
// hold an icon). Rendered as characters they're at the mercy of whatever font
// the viewer resolves — most draw them hairline-thin with no true bold cut, so
// inside a bold heading they stay spindly. Draw them with the SAME FontAwesome
// arrows the Causes/Insights tab uses instead, so the saved note matches the
// tab it was gathered from. renderArrows also covers ▲ / ▼, so notes written
// before the textarea switched to ↑ / ↓ pick up the arrows too. Storage is
// untouched — this is the display path only; Copy and the AI export still get
// plain characters.

// Parse a string's inline markers into React nodes. Newlines are preserved as
// literal characters, so the surrounding element must keep `white-space:
// pre-wrap` for line breaks — and the practitioner's own indentation — to show. (pre-LINE would collapse runs of spaces, so a hand-aligned line came
// out squashed once it left the textarea.)
export function renderInlineMarkdown(text: string): React.ReactNode {
  if (!text) return text;
  const nodes: React.ReactNode[] = [];
  let buf = '';
  const flush = () => {
    if (buf) {
      nodes.push(
        <React.Fragment key={`t-${nodes.length}`}>
          {renderArrows(buf, `p-${nodes.length}`)}
        </React.Fragment>,
      );
      buf = '';
    }
  };
  let i = 0;
  while (i < text.length) {
    let m: { inner: string; end: number } | null = null;
    let tag: 'strong' | 'em' | 'u' | null = null;
    if (text.startsWith('**', i)) {
      m = matchMarker(text, i, '**');
      tag = 'strong';
    }
    if (!m && text[i] === '*') {
      m = matchMarker(text, i, '*');
      tag = 'em';
    }
    if (!m && text[i] === '_') {
      m = matchMarker(text, i, '_');
      tag = 'u';
    }
    if (m) {
      flush();
      const key = `md-${i}`;
      const inner = renderArrows(m.inner, key);
      if (tag === 'strong') nodes.push(<strong key={key}>{inner}</strong>);
      else if (tag === 'em') nodes.push(<em key={key}>{inner}</em>);
      else nodes.push(<u key={key}>{inner}</u>);
      i = m.end;
    } else {
      buf += text[i];
      i++;
    }
  }
  flush();
  return <>{nodes}</>;
}

// Block-level rendering shared by every place a saved note is displayed (the
// per-section Notes tab, the Insights panel and the consolidated Section Notes
// panel), so one note reads the same everywhere:
//   "## Label"    -> heading (h6)
//   anything else -> body text, inline markers parsed, newlines preserved
// Every note reads in the same ink, whichever surface it's on: the softer
// #495057 a note has always had while being typed (Bootstrap's .form-control
// colour), rather than the near-black #212529 of ordinary body text. Rendering
// a saved note in body ink made it look heavier than the same note in its
// editor.
export const NOTE_TEXT_COLOR = '#495057';

/**
 * Wrapping for note text: both the legacy property and the standard one.
 *
 * A note is free text, so a practitioner can put a run in it with no space and
 * no punctuation to break at — a pasted token, a code typed without hyphens,
 * or just a hand resting on the keyboard. Without this, that run does not wrap
 * at all: on screen it walks out through the side of its card, and in the PDF
 * it is drawn as one line off the edge of the page. (Text with ordinary break
 * opportunities is fine either way — a URL breaks after its slashes and a
 * hyphenated lab code after its hyphens, in the browser and in html2canvas
 * alike. It is the run with NO break opportunity that needs this.)
 *
 * Both properties, because the two renderers read different ones. Chrome
 * treats the legacy `word-break: break-word` as an alias for
 * `overflow-wrap: break-word`; html2canvas, which rasterizes both PDFs, breaks
 * inside a word only on the computed `overflow-wrap` — its own `word-break`
 * parser knows `break-all`, `keep-all` and `normal` and reads `break-word` as
 * `normal` (breakWords() in html2canvas/dist/html2canvas.js). Setting only the
 * legacy one looks correct on screen and in the export preview, and fails in
 * the exported file.
 *
 * It is an inherited property, so a container carrying it covers the nodes
 * rendered inside it — which is how the callers that render a note LINE BY
 * LINE (renderNoteLine) get it, the same way they get `white-space: pre-wrap`.
 */
export const NOTE_WRAP: React.CSSProperties = {
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
};

export const NOTE_HEADING_STYLE: React.CSSProperties = {
  ...NOTE_WRAP,
  marginBottom: 6,
  marginLeft: 8,
  fontSize: '1rem',
  fontWeight: 'bold',
  color: NOTE_TEXT_COLOR,
  // Keep a section label with the notes beneath it — never orphan it at the
  // bottom of a PDF page or split it across the page break.
  pageBreakAfter: 'avoid',
  pageBreakInside: 'avoid',
};

export function renderNoteBody(source: string): JSX.Element {
  const lines = (source || '').split('\n');
  const nodes: JSX.Element[] = [];
  let buf: string[] = [];

  const flush = (key: string | number) => {
    // Trim only the outer blank lines so a section's internal spacing (e.g.
    // bullet lists the practitioner pasted in) is preserved via pre-wrap —
    // including any spaces they used to indent or align a line.
    const text = buf.join('\n').replace(/^\n+|\n+$/g, '');
    if (text.trim()) {
      nodes.push(
        <div
          key={`p-${key}`}
          style={{
            whiteSpace: 'pre-wrap',
            ...NOTE_WRAP,
            marginBottom: 14,
            marginLeft: 8,
            color: NOTE_TEXT_COLOR,
          }}
        >
          {renderInlineMarkdown(text)}
        </div>,
      );
    }
    buf = [];
  };

  lines.forEach((line, idx) => {
    if (line.startsWith('## ')) {
      flush(idx);
      nodes.push(
        <h6
          key={`h6-${idx}`}
          style={{ ...NOTE_HEADING_STYLE, marginTop: nodes.length > 0 ? 14 : 0 }}
        >
          {line.slice(3).trim()}
        </h6>,
      );
    } else {
      buf.push(line);
    }
  });
  flush('end');

  // The 14px block margin is separation BETWEEN blocks, so the last block drops
  // it. Left on, it printed as dead space inside the bottom of every note card
  // — it read as an extra blank line under the text, and stacked on the card
  // body's own 20px bottom padding to make the gap under a note twice the one
  // above it. The editor never showed it because the textarea renders the raw
  // string; it only appeared once the note was saved and rendered.
  const last = nodes[nodes.length - 1];
  if (last) {
    nodes[nodes.length - 1] = React.cloneElement(last, {
      style: { ...(last.props.style || {}), marginBottom: 0 },
    });
  }

  return <>{nodes}</>;
}

// One line of a note, for the callers that render (and paginate) a note LINE BY
// LINE — the comparison report's "Insights by Collection Date" table and its
// "Overall Progress & Trends" block both split a note into lines and measure
// them. A "## Label" line becomes bold, i.e. the same weight and size as
// renderNoteBody's heading, but still exactly one line so the caller's line math
// holds. The container must keep `white-space: pre-wrap`.
export function renderNoteLine(line: string): React.ReactNode {
  // A blank line keeps its height, as it did before markup rendered here.
  if (!line) return '\u00A0';
  if (line.startsWith('## ')) return <strong>{line.slice(3).trim()}</strong>;
  return renderInlineMarkdown(line);
}

// Wrap the current selection of a textarea in `marker`, returning the new value
// and the selection to restore so the wrapped text stays highlighted. With an
// empty selection it drops the markers at the caret ready to type between them.
export function wrapSelection(
  el: HTMLTextAreaElement,
  marker: FormatMarker,
): { value: string; selStart: number; selEnd: number } {
  const value = el.value ?? '';
  const s = el.selectionStart ?? value.length;
  const e = el.selectionEnd ?? value.length;
  const before = value.slice(0, s);
  const sel = value.slice(s, e);
  const after = value.slice(e);
  return {
    value: `${before}${marker}${sel}${marker}${after}`,
    selStart: s + marker.length,
    selEnd: e + marker.length,
  };
}

// Map a Bold/Italic/Underline keyboard shortcut (Cmd/Ctrl+B/I/U) to its marker,
// or null when the event isn't a formatting shortcut.
export function formatMarkerForKeydown(
  e: React.KeyboardEvent,
): FormatMarker | null {
  if (!(e.metaKey || e.ctrlKey) || e.altKey) return null;
  switch (e.key.toLowerCase()) {
    case 'b':
      return '**';
    case 'i':
      return '*';
    case 'u':
      return '_';
    default:
      return null;
  }
}

type ToolbarProps = {
  // Returns the textarea the toolbar formats. A getter (not a ref) so the same
  // toolbar works for any host component that owns its own textarea.
  getTextarea: () => HTMLTextAreaElement | null;
  // Called with the formatted value and the selection to restore.
  onApply: (value: string, selStart: number, selEnd: number) => void;
  disabled?: boolean;
};

// A small Bold / Italic / Underline button group. Uses onMouseDown +
// preventDefault so clicking a button doesn't blur the textarea and lose the
// selection before the format is applied.
export class MarkdownToolbar extends React.Component<ToolbarProps> {
  private apply = (marker: FormatMarker) => {
    const el = this.props.getTextarea();
    if (!el) return;
    const { value, selStart, selEnd } = wrapSelection(el, marker);
    this.props.onApply(value, selStart, selEnd);
  };

  private button(marker: FormatMarker, label: string, css: React.CSSProperties, title: string) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline-secondary"
        disabled={this.props.disabled}
        onMouseDown={e => {
          e.preventDefault();
          this.apply(marker);
        }}
        title={title}
        style={{ minWidth: 34, ...css }}
      >
        {label}
      </Button>
    );
  }

  render() {
    return (
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {this.button('**', 'B', { fontWeight: 'bold' }, 'Bold (Ctrl/Cmd+B)')}
        {this.button('*', 'I', { fontStyle: 'italic' }, 'Italic (Ctrl/Cmd+I)')}
        {this.button('_', 'U', { textDecoration: 'underline' }, 'Underline (Ctrl/Cmd+U)')}
      </div>
    );
  }
}
